import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Form, InputNumber, Select, theme, Typography } from 'antd';
import { type ReactNode } from 'react';
import { type WizardItemValue } from './site-menu-order-wizard.mapper';

export interface ItemOption {
  id: number;
  name: string;
  description: string | null;
}

interface OrderItemsListProps {
  name: 'beverages' | 'extraBeverages' | 'supplies';
  itemKey: 'beverage' | 'supply';
  items: ItemOption[];
  placeholder: string;
  missingItemMessage: string;
  addLabel: string;
  showDescription?: boolean;
}

/** The beverages or supplies ordered with a meal: one row each, with its quantity */
const OrderItemsList = ({
  name,
  itemKey,
  items,
  placeholder,
  missingItemMessage,
  addLabel,
  showDescription = false,
}: OrderItemsListProps): ReactNode => {
  const { token } = theme.useToken();
  const rows: Array<WizardItemValue | undefined> = Form.useWatch(name) ?? [];
  const selectedIds = rows.map((row) => row?.[itemKey]?.id);

  // An item can be picked once: the other rows don't offer the items already picked
  const optionsForRow = (rowIndex: number): Array<{ label: string; value: string }> =>
    items
      .filter((item) => {
        const id = String(item.id);
        return !selectedIds.includes(id) || selectedIds[rowIndex] === id;
      })
      .map((item) => ({ label: item.name, value: String(item.id) }));

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <Flex vertical gap="small">
          {fields.map(({ key, name: rowIndex, ...restField }) => (
            <Flex key={key} style={{ width: '100%' }} align="flex-start">
              <MinusCircleOutlined
                style={{ marginTop: 10, marginRight: token.marginMD, color: 'red' }}
                onClick={() => {
                  remove(rowIndex);
                }}
              />
              <Flex
                align="center"
                justify="flex-start"
                style={{ width: showDescription ? '30%' : '50%' }}
              >
                <Form.Item
                  {...restField}
                  name={[rowIndex, itemKey, 'id']}
                  rules={[{ required: true, message: missingItemMessage }]}
                >
                  <Select
                    allowClear
                    showSearch
                    style={{ minWidth: 300 }}
                    placeholder={placeholder}
                    optionFilterProp="label"
                    options={optionsForRow(rowIndex)}
                  />
                </Form.Item>
              </Flex>
              {showDescription && (
                <Flex align="center" justify="flex-start" style={{ width: '30%' }}>
                  <Typography.Text type="secondary">
                    {items.find((item) => String(item.id) === selectedIds[rowIndex])?.description}
                  </Typography.Text>
                </Flex>
              )}
              <Flex align="center" justify="flex-start" style={{ width: '30%' }}>
                <Form.Item
                  {...restField}
                  name={[rowIndex, 'quantity']}
                  rules={[{ required: true, message: 'Missing Quantity' }]}
                >
                  <InputNumber min={1} placeholder="Quantity" precision={0} />
                </Form.Item>
              </Flex>
            </Flex>
          ))}
          <Form.Item>
            <Button
              type="dashed"
              style={{ margin: '0 8px', maxWidth: 250 }}
              size="large"
              onClick={() => {
                add({ quantity: 1 });
              }}
              block
              icon={<PlusOutlined />}
            >
              {addLabel}
            </Button>
          </Form.Item>
        </Flex>
      )}
    </Form.List>
  );
};

export default OrderItemsList;
